/* eslint-disable camelcase */
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { Grid } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';

import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { Helper } from '../../../common/helpers/Helper';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { AppConfig } from '../../../AppConfig';
import { AppStyles, AppTheme } from '../../../styles/AppTheme';
import { AppUiHelper } from '../../../helpers/AppUiHelper';
import { SubEntityCard } from '../../../common/components/SubEntity/SubEntityCard';
import { SubEntityList } from '../../../common/components/SubEntity/SubEntityList';
import WhatsappTemplatePreview from '../../../common/components/WhatsappTemplatePreview';
import { buildPreviewComponentsFromTemplate } from './WhatsappPreviewAdapter';
import { buildWaNumberLabel, buildWaNumberSelectOptions, extractCrmNumbersItems, getWaOrgAccountNumbers, withVendorProjectIdQueryParam } from './WhatsappUtils';

const TYPE_TEMPLATE_INSTANCE = 'TEMPLATE_INSTANCE'

function isWaContentLive(state) {
  return String(state || '').toUpperCase() === 'LIVE'
}

function getWaTemplateComponentText(current) {
  if (current.text) {
    return current.text
  } else if (current.type === 'BUTTONS' && current.buttons) {
    return current.buttons.map(b => [b.type, b.text + (b.url ? ` [${b.url}]` : '')].filter(s => !!s).join(': ')).join('\n')
  } else if (current.format) {
    return `{{${String(current.format).toLowerCase()}}}`
  }
  return ''
}

function getWhatsAppMetadataCardFieldDef() {
  return {
    title: 'Template',
    steps: [
      {
        fields: [
          {
            key: 'wabaNumberDisplay',
            type: 'edit_text',
            label: 'WABA Number',
            tip: 'WhatsApp Business Account phone number',
            disabled: true,
            placement: 'left',
          },
          {
            key: 'templateId',
            type: 'edit_text',
            label: 'Template',
            tip: 'WhatsApp template',
            disabled: true,
            placement: 'right',
          },
        ],
      },
    ],
  }
}

function getWhatsAppMetadataFieldDef(options) {
  const { waAccounts, waTemplates = [], orgId, ctaButtonText } = options
  const waOrgAccounts = waAccounts && waAccounts[orgId]
  const { fromNumbers, waNumberDetails } = getWaOrgAccountNumbers(waOrgAccounts)
  return {
    title: 'Template',
    ctaButtonText: ctaButtonText || 'Next',
    steps: [
      {
        fields: [
          {
            key: 'wabaNumber',
            type: 'radio',
            label: 'WABA Number',
            tip: 'Select the WABA number',
            options: buildWaNumberSelectOptions(fromNumbers, waNumberDetails),
            v_required: {
              value: true,
              err: 'Provide WhatsApp account',
            },
            placement: 'left',
          },
          {
            key: 'templateId',
            type: 'radio',
            label: 'Template',
            tip: 'Select WhatsApp template',
            options: waTemplates.map(t => ({ label: t.name, key: t.name })),
            v_required: {
              value: true,
              err: 'Provide WhatsApp template',
            },
            placement: 'right',
          },
        ],
      },
    ],
  }
}

function getFieldDefWATemplateVariable(current, crmContactsFieldDef = null) {
  const fields = []
  const currentFormat = (current && current.format) ? String(current.format).toLowerCase() : ''
  if (currentFormat === 'image') {
    fields.push({
      key: 'media',
      linkName: 'mediaLink',
      type: 'edit_text',
      label: 'Image (PNG/JPEG, max 1920px height)',
      imageHeight: 1920,
      mode: 'fit',
      tip: 'Upload an image in PNG or JPEG format',
      v_required: { value: true, err: 'Provide image' },
      v_min: { value: 1, err: 'Invalid image' },
      v_max: { value: 100, err: 'Invalid image' },
      imageUpload: true,
      uploadType: 'media',
      uploadAccept: 'image/png, image/jpeg',
      placement: 'left',
    })
  } else if (currentFormat === 'text') {
    fields.push({
      key: 'text',
      type: 'radio',
      label: 'Contact Variable',
      tip: 'Choose one of the contact variables (or enter a custom text)',
      options: AppUiHelper.getContactTemplateVariables(crmContactsFieldDef).map(ctv => ({ key: ctv.value, label: ctv.text })).concat({ key: 'custom', label: '[Custom Text]' }),
      v_required: {
        value: true,
        err: 'Choose one of the contact variables (or enter a custom text)',
      },
      placement: 'left',
    })
    fields.push({
      condition: { key: 'text', value: 'custom', values: ['custom'] },
      key: 'customText',
      type: 'edit_text',
      label: 'Custom Value',
      tip: 'Enter any text',
      v_required: {
        value: true,
        err: 'Provide custom value',
      },
      placement: 'left',
    })
  } else if (currentFormat === 'video') {
    fields.push({
      key: 'media',
      label: 'Video (MP4, max 16MB file size)',
      hint: 'Upload a video in MP4 format',
      v_required: {
        value: true,
        err: 'Provide video',
      },
      placement: 'left',
      type: 'edit_text',
      subType: 'VIDEO',
      documentUpload: true,
      linkName: 'mediaLink',
      v_min: {
        value: 1,
        err: 'Invalid video',
      },
      v_max: {
        value: 100,
        err: 'Invalid video',
      },
      uploadType: 'media',
      uploadAccept: 'video/mp4',
      fileSizeLimit: 16000000,
    })
  } else if (currentFormat === 'document') {
    fields.push({
      key: 'media',
      linkName: 'mediaLink',
      label: 'Document',
      hint: 'Upload PDF document (max 20MB)',
      v_required: { value: true, err: 'Provide document' },
      placement: 'left',
      type: 'edit_text',
      documentUpload: true,
      v_min: { value: 1, err: 'Invalid document' },
      v_max: { value: 100, err: 'Invalid document' },
      uploadType: 'media',
      uploadAccept: 'application/pdf',
      fileSizeLimit: 20000000,
    })
    fields.push({
      key: 'fileName',
      type: 'edit_text',
      label: 'File Name',
      tip: 'Set the document filename to send',
      v_required: { value: true, err: 'Provide file name' },
      placement: 'right',
    })
  }

  return {
    title: 'Edit',
    ctaButtonText: 'Save',
    steps: [{ fields }],
  }
}

function renderWaTemplateVariablesValue(current) {
  const currentFormat = (current && current.format) ? String(current.format).toLowerCase() : ''
  if (current && currentFormat === 'image' && current.mediaLink) {
    return (
      <div className={css(Styles.imageWrapper)}>
        <img
          src={current.mediaLink}
          alt={current.media || current.mediaLink || 'Preview'}
          className={css(Styles.imagePreview)}
        />
      </div>
    )
  }
  if (currentFormat === 'video' && current.mediaLink) {
    return (
      <div className={css(Styles.videoWrapper)}>
        <video
          src={current.mediaLink}
          controls
          controlsList='nodownload noplaybackrate'
          className={css(Styles.videoPreview)}
        />
      </div>
    )
  }
  if (current && currentFormat === 'document' && current.mediaLink) {
    return (
      <div>
        <a href={current.mediaLink} target='_blank' rel='noopener noreferrer' className={css(Styles.spanValue)}>
          Open document
        </a>
      </div>
    )
  }
  if (current && currentFormat === 'text' && current.text) {
    return (
      <div>
        <span className={css(Styles.spanValue)}>{current.text}</span>
      </div>
    )
  }

  return (
    <div>
      <span className={css(Styles.spanValue)}>{current.text || current.mediaLink || current.media || '[Not Set]'}</span>
    </div>
  )
}

function getContentDetailFieldDef(baseFieldDef) {
  if (!baseFieldDef) {
    return {
      title: 'Content Detail',
      ctaButtonText: 'Save',
      steps: [{
        fields: [
          {
            key: 'title',
            type: 'edit_text',
            label: 'Nickname',
            v_required: { value: true, err: 'Provide nickname' },
            placement: 'left',
          },
          {
            key: 'state',
            type: 'radio',
            label: 'Status',
            options: [
              { key: 'LIVE', label: 'LIVE' },
              { key: 'DISABLED', label: 'DISABLED' },
            ],
            v_required: { value: true, err: 'Choose status' },
            placement: 'right',
          },
        ],
      }],
    }
  }
  const copy = JSON.parse(JSON.stringify(baseFieldDef))
  const fields = copy.steps && copy.steps[0] && Array.isArray(copy.steps[0].fields) ? copy.steps[0].fields : []
  copy.steps[0].fields = fields.filter(f => ['title', 'state'].includes(f.key))
  return UiHelper.reflowFields(copy)
}

function normalizeWaTemplatesResponse(data) {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (Array.isArray(data.items)) return data.items
  if (Array.isArray(data.templates)) return data.templates
  return []
}

/** Meta-style template components (HEADER / BODY / …), array or dict. */
function getWaTemplateStructureComponents(content) {
  const wt = content && content.waTemplate
  const components = wt && wt.components
  if (!components) return []
  if (Array.isArray(components)) return components
  if (typeof components === 'object') return Helper.dictToArray(components)
  return []
}

export class WhatsappContentDetail extends BaseEntityPageSlixta {
  constructor(props) {
    super(props, { assetPicker: true })
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = 'WhatsApp content detail'
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.contentId = this.props.match.params.id
    this.breadCrumbs = [
      { title: 'WhatsApp messages', to: `${this.baseRoute()}/whatsapp-messages`, type: 'button', icon: 'arrow_back_ios' },
    ]
    this.toFetch = [
      {
        key: 'waContent',
        ms: this.ms,
        method: 'GET',
        paths: ['wacontents', this.contentId],
        queryParams: { orgId: this.orgId, pb: 'fieldDef,waSessionMessageFieldDef,waTemplate' },
      },
      {
        key: 'waAccounts',
        ms: this.ms,
        method: 'GET',
        paths: ['miscs', 'get-wa-accounts'],
        queryParams: { orgId: this.orgId },
      },
      {
        key: 'crmContacts',
        ms: this.ms,
        method: 'GET',
        paths: ['crmcontacts'],
        queryParams: { orgId: this.orgId, pb: 'fieldDef', includeCustomFields: 'true', limit: 1 },
      },
      {
        key: 'crmnumbers',
        ms: this.ms,
        method: 'GET',
        paths: ['crmnumbers'],
        queryParams: { orgId: this.orgId },
      },
    ]
    this.apiData = { orgId: this.orgId }
    this.pageKey = this.pageTitle
    this.noAdd = true
    this.contentDetailFieldDef = null
    this.whatsappMetadataFieldDef = null
    this.whatsappMetadataCardFieldDef = getWhatsAppMetadataCardFieldDef()
    this.waNumberDetails = undefined
    this.waTemplateHeaders = {
      Index: current => current.__index + 1,
      Type: current => <span className={css(Styles.spanValue)}>{String(current.type || '').toUpperCase()}</span>,
      Format: current => <span className={css(Styles.spanValue)}>{String(current.format || 'TEXT').toUpperCase()}</span>,
      Value: current => <span className={css(Styles.spanValue, Styles.preWrapText)}>{getWaTemplateComponentText(current)}</span>,
    }
    this.waTemplateVariablesHeaders = {
      Index: current => current.__index + 1,
      Type: current => <span className={css(Styles.spanValue)}>{String(current.type || '').toUpperCase()}</span>,
      Format: current => <span className={css(Styles.spanValue)}>{String(current.format || '').toUpperCase() + (current.vIndex ? ` {{${current.vIndex}}}` : '')}</span>,
      Value: current => renderWaTemplateVariablesValue(current),
    }
    this.state = {
      ...this.state,
      isUpdatingContentState: false,
    }
  }

  setHeaderActions() {
    const isLive = isWaContentLive(this.content && this.content.state)
    const updating = this.state.isUpdatingContentState
    this.headerActions = []
    if (!isLive) {
      this.headerActions.push({
        label: 'Enable',
        tooltip: 'Enable',
        icon: 'send',
        type: 'icon-label',
        disabled: updating,
        onClick: () => this.updateContentState('LIVE'),
      })
    } else {
      this.headerActions.push({
        label: 'Disable',
        tooltip: 'Disable',
        icon: 'pause',
        type: 'icon-label',
        disabled: updating,
        onClick: () => this.updateContentState('DISABLED'),
      })
    }
    this.headerActions.push({
      label: 'Refresh',
      tooltip: 'Refresh',
      icon: 'refresh',
      type: 'icon-label',
      onClick: () => this.fetchItems(),
    })
  }

  refreshHeaderActions() {
    this.setHeaderActions()
    this.setState({})
  }

  async updateContentState(state) {
    if (!this.content || !this.content.id || this.state.isUpdatingContentState) return
    this.setState({ isUpdatingContentState: true }, () => this.refreshHeaderActions())
    try {
      await ApiHelper.callAwait({
        ms: this.ms,
        method: 'PUT',
        paths: ['wacontents', this.content.id],
        queryParams: { orgId: this.orgId },
        jsonBody: {
          orgId: this.orgId,
          title: this.content.title || '',
          state,
        },
      })
      this.content = { ...this.content, state }
      const name = String(this.content.title || '').trim() || 'Message'
      this.setState({
        isUpdatingContentState: false,
        showToast: true,
        isErrorToast: false,
        toastMsg: `Message "${name}" is now ${state}`,
      }, () => this.refreshHeaderActions())
    } catch (err) {
      this.setState({
        isUpdatingContentState: false,
        showToast: true,
        isErrorToast: true,
        toastMsg: Helper.getErrorMsg(err),
      }, () => this.refreshHeaderActions())
    }
  }

  async onFetchSuccess(results) {
    this.content = this.getResult(results, 'waContent')
    this.waAccounts = this.getResult(results, 'waAccounts')
    this.crmNumbers = extractCrmNumbersItems(this.getResult(results, 'crmnumbers'))
    const waOrgAccounts = this.waAccounts && this.waAccounts[this.orgId]
    const { waNumberDetails } = getWaOrgAccountNumbers(waOrgAccounts)
    this.waNumberDetails = waNumberDetails
    this.crmContactsFieldDef = this.getResult(results, 'crmContacts') && this.getResult(results, 'crmContacts').fieldDef
    this.contentDetailFieldDef = getContentDetailFieldDef(this.content && this.content.fieldDef)
    this.whatsappMetadataFieldDef = getWhatsAppMetadataFieldDef({
      waAccounts: this.waAccounts || {},
      orgId: this.orgId,
      waTemplates: [],
      ctaButtonText: 'Next',
    })
    this.pageTitle = this.content && this.content.title ? this.content.title : 'WhatsApp content detail'
    this.setHeaderActions()
    const isTemplateInstance = String(this.content && this.content.iType || '').toUpperCase() === TYPE_TEMPLATE_INSTANCE
    let waTemplateStructureItems = isTemplateInstance ? getWaTemplateStructureComponents(this.content) : []
    if (
      isTemplateInstance
      && waTemplateStructureItems.length === 0
      && this.content
      && this.content.wabaNumber
      && this.content.templateId
    ) {
      try {
        const data = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'GET',
          paths: ['hooks'],
          queryParams: withVendorProjectIdQueryParam({
            orgId: this.orgId,
            fromNumber: this.content.wabaNumber,
            bridgePath: 'whatsapptemplates',
          }, this.content.wabaNumber, this.crmNumbers),
        })
        const templates = normalizeWaTemplatesResponse(data).filter(t => String(t.status || '').toUpperCase() === 'APPROVED')
        const match = templates.find(t => t.name === this.content.templateId)
        if (match) {
          const fromHook = match.components
          if (Array.isArray(fromHook) && fromHook.length) {
            waTemplateStructureItems = fromHook
          } else if (fromHook && typeof fromHook === 'object') {
            waTemplateStructureItems = Helper.dictToArray(fromHook)
          }
        }
      } catch (e) {
        /* keep empty; detail still usable */
      }
    }
    this.setState({
      items: [],
      waTemplateStructureItems,
    }, () => {
      if (isTemplateInstance && (!this.content.wabaNumber || !this.content.templateId) && !this.state.modalOpen) {
        this.openModalForEditWhatsAppMetaData(this.content)
      }
    })
  }

  openModalForEditContent = (current) => {
    this.formDefinition = JSON.parse(JSON.stringify(this.contentDetailFieldDef))
    this.formDefinition.formType = 'edit'
    this.formDefinition.title = 'Content Detail'
    Helper.populateFormDefinitionValues(this.formDefinition, current)
    this.setState({
      apiMethod: 'PUT',
      apiData: { orgId: this.orgId },
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['wacontents', this.contentId], queryParams: { orgId: this.orgId } }),
      modalOpen: true,
    })
  }

  openModalForEditWhatsAppTemplate(current, templates) {
    let fieldDef = getWhatsAppMetadataFieldDef({
      waAccounts: this.waAccounts,
      waTemplates: templates,
      orgId: this.orgId,
      ctaButtonText: 'Save',
    })
    fieldDef = {
      ...fieldDef,
      steps: fieldDef.steps.map(step => ({
        ...step,
        fields: [step.fields[1]],
      })),
    }
    this.formDefinition = fieldDef
    this.formDefinition.formType = 'edit'
    Helper.populateFormDefinitionValues(this.formDefinition, current)
    this.setState({
      apiData: {
        waContent: current || {},
        templates: templates || [],
        isEditWhatsAppTemplate: true,
      },
      modalOpen: true,
    })
  }

  openModalForEditWhatsAppMetaData(current) {
    let fieldDef = getWhatsAppMetadataFieldDef({
      waAccounts: this.waAccounts,
      orgId: this.orgId,
    })
    fieldDef = {
      ...fieldDef,
      steps: fieldDef.steps.map(step => ({
        ...step,
        fields: [step.fields[0]],
      })),
    }
    this.formDefinition = fieldDef
    this.formDefinition.formType = 'edit'
    Helper.populateFormDefinitionValues(this.formDefinition, current)
    this.setState({
      apiData: {
        waContent: current || {},
        isEditWhatsAppMetaData: true,
      },
      modalOpen: true,
    })
  }

  openModalForEditWATemplateVariable(current) {
    current = JSON.parse(JSON.stringify(current))
    if (current && String(current.format || '').toLowerCase() === 'document' && (!current.fileName || !String(current.fileName).trim())) {
      current.fileName = 'Untitled'
    }
    if (current.text && AppUiHelper.getContactTemplateVariables(this.crmContactsFieldDef).every(ctv => ctv.value !== current.text)) {
      current.customText = current.text
      current.text = 'custom'
    }
    this.formDefinition = getFieldDefWATemplateVariable(current, this.crmContactsFieldDef)
    this.formDefinition.formType = 'edit'
    Helper.populateFormDefinitionValues(this.formDefinition, current)
    this.setState({
      apiMethod: 'PUT',
      apiData: { entityAction: 'edit', entityAttrName: 'components', id: current.id, value: current.value, orgId: this.orgId },
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['wacontents', this.contentId], queryParams: { orgId: this.orgId } }),
      modalOpen: true,
    })
  }

  async customSubmitForWhatsAppMetaData(options) {
    const { formData } = options
    delete formData.isEditWhatsAppMetaData
    if (formData && this.waAccounts) {
      const selectedFromNumber = formData.wabaNumber
      const waTemplates = await ApiHelper.callAwait({
        ms: this.ms,
        method: 'GET',
        paths: ['hooks'],
        queryParams: withVendorProjectIdQueryParam({
          orgId: this.orgId,
          fromNumber: selectedFromNumber,
          bridgePath: 'whatsapptemplates',
        }, selectedFromNumber, this.crmNumbers),
      }).then(data => normalizeWaTemplatesResponse(data).filter(template => template.status === 'APPROVED'))
      const waContent = {
        ...formData.waContent,
        wabaNumber: selectedFromNumber,
      }
      if (selectedFromNumber !== this.content.wabaNumber) {
        waContent.templateId = ''
      }
      this.setState({
        modalOpen: false,
      }, () => {
        this.openModalForEditWhatsAppTemplate(waContent, waTemplates)
      })
    } else {
      this.setState({ modalOpen: false })
    }
  }

  async customSubmitForEditWhatsAppTemplate(options) {
    const { formData } = options
    delete formData.isEditWhatsAppTemplate
    if (formData) {
      const selectedTemplate = formData.templateId
      const waContent = {
        ...formData.waContent,
        templateId: selectedTemplate,
      }
      if (waContent && waContent.wabaNumber && waContent.templateId) {
        ApiHelper.callAwait({
          ms: this.ms,
          method: 'PUT',
          jsonBody: { orgId: this.orgId, wabaNumber: waContent.wabaNumber, templateId: waContent.templateId },
          paths: ['wacontents', this.contentId],
          queryParams: { orgId: this.orgId },
        }).then(() => {
          this.setState({
            fetchState: ApiHelper.State.READY,
            modalOpen: false,
          }, () => this.fetchItems())
        }).catch(err => {
          const errorMessage = Helper.getErrorMsg(err)
          this.setState({
            fetchState: ApiHelper.State.ERROR,
            modalOpen: false,
            errMsg: errorMessage,
          })
        })
      } else {
        this.setState({
          fetchState: ApiHelper.State.READY,
          modalOpen: false,
        })
      }
    }
  }

  async customSubmitForEditWATemplateVariable(options) {
    const { formData, caller } = options
    const apiData = JSON.parse(JSON.stringify(formData))
    if (apiData.text === 'custom') {
      apiData.text = apiData.customText
    }
    const currentFormat = apiData.format ? String(apiData.format).toLowerCase() : ''
    if (currentFormat === 'document' && (apiData.media || apiData.mediaLink) && (!apiData.fileName || !String(apiData.fileName).trim())) {
      apiData.fileName = 'Untitled'
    }
    return caller.submit.call(caller, apiData, true)
  }

  async customSubmit(options) {
    const { formData, caller } = options
    if (formData.isEditWhatsAppMetaData) return this.customSubmitForWhatsAppMetaData(options)
    if (formData.isEditWhatsAppTemplate) return this.customSubmitForEditWhatsAppTemplate(options)
    if (formData.entityAttrName === 'components') return this.customSubmitForEditWATemplateVariable(options)
    return caller.submit.call(caller, formData, true)
  }

  waTemplateSubEntityActions = (current) => {
    return [
      <Tooltip title='Edit Variable' placement='top-start' key={`${current.id}-editTT`}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i className='material-icons-outlined' onClick={() => this.openModalForEditWATemplateVariable(current)}>edit</i>
        </span>
      </Tooltip>,
    ]
  }

  renderAboveTable() {
    const isTemplateInstance = String(this.content && this.content.iType || '').toUpperCase() === TYPE_TEMPLATE_INSTANCE
    const waSettings = {
      wabaNumber: this.content && this.content.wabaNumber || '',
      wabaNumberDisplay: buildWaNumberLabel(this.content && this.content.wabaNumber, this.waNumberDetails) || '',
      templateId: this.content && this.content.templateId || '',
      components: this.content && this.content.components || {},
    }
    const variableItems = Helper.sortDictArray(Helper.dictToArray(waSettings.components || {}), 'id')

    return (
      <div className={css(Styles.containerNarrow)}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <SubEntityCard
              renderFieldsRowwise
              title='Content Detail'
              formDefinition={this.contentDetailFieldDef}
              item={this.content || {}}
              openModalForEdit={this.openModalForEditContent}
            />
          </Grid>
          {isTemplateInstance && (
            <Grid item xs={12} sm={6}>
              <SubEntityCard
                renderFieldsRowwise
                title='Template'
                formDefinition={this.whatsappMetadataCardFieldDef}
                item={waSettings}
                openModalForEdit={this.openModalForEditWhatsAppMetaData.bind(this)}
              />
            </Grid>
          )}
        </Grid>

        {isTemplateInstance && (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <SubEntityList
                title='Template Content'
                tableHeadersMap={this.waTemplateHeaders}
                items={Array.isArray(this.state.waTemplateStructureItems) ? this.state.waTemplateStructureItems : []}
                noAdd
                noEdit
                noDelete
                fetchState={this.state.fetchState}
              />
            </Grid>
          </Grid>
        )}

        {isTemplateInstance && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <SubEntityList
                title='Template Variables'
                tableHeadersMap={this.waTemplateVariablesHeaders}
                items={variableItems}
                subEntityActions={this.waTemplateSubEntityActions}
                noAdd
                fetchState={this.state.fetchState}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <div className={css(Styles.previewPane)}>
                <div className={css(Styles.previewCard)}>
                  <div className={css(Styles.previewTitle)}>Preview</div>
                  <div className={css(Styles.previewBody)}>
                    <WhatsappTemplatePreview
                      components={buildPreviewComponentsFromTemplate(
                        this.state.waTemplateStructureItems,
                        waSettings.components,
                        { hideTemplateExamplesWhenUnset: true }
                      )}
                    />
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        )}
      </div>
    )
  }
}

const Styles = StyleSheet.create({
  line2: {
    fontSize: 12,
    color: 'gray',
  },
  spanValue: {
    fontSize: 12,
  },
  preWrapText: {
    whiteSpace: 'pre-wrap',
  },
  imagePreview: {
    width: '100px',
    height: 'auto',
    display: 'block',
  },
  videoPreview: {
    width: '100px',
    height: 'auto',
    display: 'block',
  },
  imageWrapper: {
    display: 'block',
  },
  videoWrapper: {
    display: 'block',
  },
  containerNarrow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: AppTheme.viewPort,
    paddingBottom: 50,
    width: '100%',
  },
  previewPane: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
  previewCard: {
    width: '100%',
    minHeight: '100%',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
    padding: 16,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },
  previewTitle: {
    width: '100%',
    fontSize: 15,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 12,
    paddingLeft: 2,
  },
  previewBody: {
    width: '100%',
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
})
